import { useAppSelector, useAppDispatch } from "@/services/hooks";
import React, { useEffect, useReducer } from "react";

import { AddingAd,clearMessage } from "@/services/adServices/adActions";
import { getClients } from "@/services/clientService/clientActions";
import { getAllProducts } from "@/services/productServices/productActions";
import { useImageUpload } from "@/services/ImageServices/useImageUpload";
import NotificationAlertOptions from "@/layouts/Alerts";
import AddAdsView from "./AddAdsView";
import type { PickedImage } from "../User/adding-user/ImageUploadContainer";
import { asCaught } from "@/services/caughtError";
import { adForm, addFormState, clientOptions, productOptions } from "./adForm";

const formActions = adForm.actions;

const POSITIONS = [
  { value: "right", label: "Right" },
  { value: "left", label: "Left" },
  { value: "top", label: "Top" },
  { value: "bottom", label: "Bottom " },
  { value: "inListing", label: "In Listing " },
];

export default function AddAdsContainer() {
  const dispatch = useAppDispatch();
  const uploadImage = useImageUpload();

  const productsFromStore = useAppSelector((s) => s.products.products);
  const clientsFromStore  = useAppSelector((s) => s.clients.clients);
  const message           = useAppSelector((s) => s.ad.message);
  const errors            = useAppSelector((s) => s.errors);
  const site              = useAppSelector((s) => s.sites.site);

  // SCRUM-2219: the form state is one createSlice (adForm.ts), mounted locally;
  // the handlers below only dispatch its named actions.
  const [form, dispatchForm] = useReducer(adForm.reducer, undefined, addFormState);
  const { ad, client, product, media: adsMedia, countryCode, loading } = form;

  // Options are derived during render from the store lists.
  const products = productOptions(productsFromStore);
  const clients = clientOptions(clientsFromStore);

  useEffect(() => {
    dispatch(getClients());
    dispatch(getAllProducts());
  }, [dispatch]);

  useEffect(() => {
    if (errors?.errors) {
      dispatchForm(formActions.saveFailed());
    }
  }, [errors, dispatch]);

  useEffect(() => {
    if (message) {
      NotificationAlertOptions("success", message);
      dispatch(clearMessage());
      dispatchForm(formActions.saved());
    }
  }, [message, dispatch]);

  const onChange = (e) => {
    const { name, value } = e.target;
    dispatchForm(formActions.fieldChanged({ name, value }));
  };

  const onSelectPosition = (opt) => dispatchForm(formActions.positionSelected(opt));
  const onSelectClient = (opt) => dispatchForm(formActions.clientSelected(opt));
  const onSelectProduct = (opt) => dispatchForm(formActions.productSelected(opt));
  const onImageUpload = (imgObj: PickedImage) => dispatchForm(formActions.imagePicked(imgObj));

  const onClickAdd = async () => {
    const payload: Record<string, unknown> = { ...ad };

    if (client && client.value)   payload.client  = client.value;
    if (product && product.value) payload.product = product.value;

    // Freshly picked file: upload first, then save inline with the returned URL
    // (useImageUpload clears images.url on success). Save `payload` (not just
    // `ad`) so a selected client/product is persisted on this branch too, like
    // the secure_url and no-image direct-save branches below. (The old watch-
    // effect spread only `ad` and silently dropped the relation on image upload.)
    if (adsMedia?.data) {
      const cc = countryCode || site?.country_code || "";
      const path = `/${cc}/BANNERS/${adsMedia.imgName}`;
      dispatchForm(formActions.uploadStarted());
      try {
        const url = await uploadImage(adsMedia.data, path);
        dispatch(AddingAd({ ...payload, adsMedia: url }));
      } catch (err) {
        dispatchForm(formActions.saveFailed());
        NotificationAlertOptions(
          "error",
          asCaught(err)?.response?.data?.message ||
            asCaught(err)?.message ||
            "Saving the ad failed. Please try again."
        );
      }
      return;
    }

    if (adsMedia?.secure_url) {
      payload.adsMedia = adsMedia.secure_url;
    }

    dispatch(AddingAd(payload));
  };

  return (
    <AddAdsView
      loading={loading}
      ad={ad}
      positions={POSITIONS}
      clients={clients}
      products={products}
      client={client}
      product={product}
      onChange={onChange}
      onSelectPosition={onSelectPosition}
      onSelectClient={onSelectClient}
      onSelectProduct={onSelectProduct}
      onClickAdd={onClickAdd}
      onImageUpload={onImageUpload}
    />
  );
}
